import { Dispatch, FC, lazy, RefObject, SetStateAction, Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import TotalBar from "./TotalBar";
import PriceDetailsSheet from "./PriceDetailsSheet";
import ReviewStep from "./ReviewStep";
import PolicyConsent from "./PolicyConsent";
import ProtectionStep from "./ProtectionStep";
import ExtrasStep from "./ExtrasStep";
import DetailsStep, { RenterField } from "./DetailsStep";
import { ProofFile } from "./ProofUpload";
import { useStepsController } from "./useStepsController";
import { NO_TAP_SELECT } from "./noTapSelect";
import { PAY, PayChoice, REVIEW, Step, STEP_DEFS } from "./steps";
import type { Stop } from "./tripStops";
import type { PayDraft } from "designs/Common/CheckOutPage/CheckOutPage";
import { EmailCodeCard } from "designs/Common/EmailCode/EmailCodeSurfaces";
import type { CodeGate } from "designs/Common/EmailCode/emailCode";

// Loaded when the payment step opens, not with the car page: the checkout pulls in the whole payment stack.
const CheckOutPagePageMain = lazy(() => import("designs/Common/CheckOutPage/CheckOutPage"));

export interface PhoneBookingProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  step: Step;
  setStep: (step: Step) => void;

  car: any;
  photo: string | null;
  /** Location, dates and times are all chosen, so the quote is for this trip. */
  ready: boolean;
  /** The quote the car page's bill shows. */
  invoice: any;
  /** The booking, once the review has made it: the Pay step shows its agreement and pays (or reserves) it, and its invoice (with any promo code) is the bill. */
  bookingdetails: any;
  madeBooking: any;
  trip: { pickup: Stop; dropoff: Stop; range: string };
  pickUp: Date | null;
  companyDetails: any;
  companyName?: string;

  protection: {
    products: any[];
    loading: boolean;
    notice?: string;
    selectedValue: any;
    onSelect: (product: any) => void;
    proof: { files: ProofFile[]; setFiles: Dispatch<SetStateAction<ProofFile[]>>; inputRef: RefObject<HTMLInputElement> };
  };
  extras: {
    extras: any[];
    addons: any[];
    selectedExtras: Set<any>;
    onExtra: (id: any, on: boolean) => void;
    selectedAddons: Set<any>;
    onAddon: (value: string) => void;
  };
  details: {
    fields: { firstName: RenterField; lastName: RenterField; email: RenterField; phone: RenterField };
    confirm?: { checked: boolean; onChange: (checked: boolean) => void; html?: string; companyName?: string };
  };

  /** The page's own checks and actions: this component draws, the page books. */
  onBookThisCar: () => void;
  checkProtection: () => boolean;
  /** Details' Continue (step 1) and the review's: the page's checks on the renter's details. */
  checkDetails: () => boolean;
  /** The review's Pay and book now ("now") or Pay later ("later"): makes (or reuses) that booking and moves on to Pay. */
  onContinueFromReview: (choice: PayChoice) => void;
  /** "Edit" on the trip: it is chosen on the car page, behind the sheet. */
  onEditTrip: () => void;
  /** The review's Edit on Details, Protection or Extras: goes there, and the step's button comes straight back. */
  onEditStep: (step: 1 | 2 | 3) => void;
  /** Sent to a step by the review's Edit: its button says "Back to review" and goes back to it. */
  returning: boolean;
  /** The consent line of the Pay step ticked, and the way to change it: the page's, so both layouts share it. */
  accepted: boolean;
  setAccepted: (next: boolean) => void;
  /** Pay now or pay later, the review's button that was pressed: it decided which booking the review made. */
  payChoice: PayChoice;
  /** The Pay step's "Reserve" (pay later): the booking exists, the renter goes to its own page. */
  onReserve: () => void;
  /** 1NOW-10770: the email code a returning renter is asked for, on the Pay step in place of the card form; null when none is. */
  codeGate: CodeGate | null;
  /** What the renter typed on the Pay step, kept above both layouts so a swap does not empty it (1NOW-11197). */
  payDraft?: PayDraft;
  setPayDraft?: Dispatch<SetStateAction<PayDraft>>;
  busy: boolean;
  busyLater: boolean;
  /** The car allows pay later. */
  payLater: boolean;
}

/**
 * Booking in steps on phones (1NOW-11155, and pay later inside it, 1NOW-11156).
 *
 * The car page keeps its state, checks and booking calls; this draws them as a
 * sticky total bar on the page and, behind "Book this car", a full-screen sheet
 * of five steps — Details, Protection, Extras, Review, Pay — with the total bar
 * and Continue on every step and the price details a tap away. Only the page's
 * existing handlers change anything: this component holds what is open and
 * which step it is on, and nothing about the booking.
 */
const PhoneBooking: FC<PhoneBookingProps> = (p) => {
  const [priceOpen, setPriceOpen] = useState(false);
  const {
    agreementUrl,
    later,
    setPay,
    payRef,
    bill,
    total,
    protectionLine,
    extraLines,
    hideExtras,
    shownSteps,
    prevStep,
    onBar,
    barLabel,
    barTo,
    payLaterButton,
    busy,
    disabled,
  } = useStepsController(p);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo?.(0, 0);
  }, [p.step]);

  useEffect(() => {
    if (!p.open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      // A sheet or popup over this one (the price sheet, the Bonzah terms) takes Escape first.
      if (e.key === "Escape" && document.querySelectorAll('[role="dialog"]').length <= 1) p.setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [p.open]);

  // Portalled to <body>: Design B wraps pages in transformed containers, which turn
  // position:fixed into "fixed to that container", so the bar and the sheet never reached the screen.
  return createPortal(
    <>
      {/* The car page: the price and "Book this car", fixed to the bottom of the screen. */}
      {!p.open &&
        (!(p.ready && total !== null) && Number(p.car?.price) > 0 ? (
          // The trip is not complete, so there is no quote yet: the day price, with the button beside it.
          <TotalBar
            total={Number(p.car.price)}
            unit="/day"
            note="Add your trip for the total"
            label="Book this car"
            onClick={p.onBookThisCar}
            fixed
          />
        ) : (
          <TotalBar total={total} onPriceDetails={() => setPriceOpen(true)} label="Book this car" onClick={p.onBookThisCar} fixed />
        ))}

      {p.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Book this car"
          className={`fixed inset-0 z-[45] flex flex-col bg-bg-color text-text-color ${NO_TAP_SELECT}`}
        >
          <div className="flex shrink-0 items-center justify-between px-2 pt-[max(8px,env(safe-area-inset-top))]">
            <button
              type="button"
              onClick={() => p.setStep(prevStep)}
              aria-label="Back"
              className={`flex h-11 w-11 items-center justify-center ${p.step === 1 ? "invisible" : ""}`}
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <span className="text-xs font-semibold text-text-color/70">
              Step {shownSteps.findIndex((d) => d.step === p.step) + 1} of {shownSteps.length}
            </span>
            <button
              type="button"
              onClick={() => p.setOpen(false)}
              aria-label="Close"
              className="flex h-11 w-11 items-center justify-center"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="shrink-0 px-4">
            <div className="flex gap-1" aria-hidden="true">
              {shownSteps.map((d) => (
                <span key={d.key} className={`h-1 flex-1 rounded-full ${d.step <= p.step ? "bg-text-color" : "bg-secondary-100"}`} />
              ))}
            </div>
            <ul className="mt-1.5 flex list-none justify-between text-[11px] text-text-color/70">
              {shownSteps.map((d) => (
                <li key={d.key} aria-current={d.step === p.step ? "step" : undefined} className={d.step === p.step ? "font-extrabold text-text-color" : ""}>
                  {d.short}
                </li>
              ))}
            </ul>
          </div>

          <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-3">
            <h2 className="mb-3 text-2xl font-extrabold leading-tight">{STEP_DEFS[p.step - 1].title}</h2>
            {p.step === 1 && <DetailsStep fields={p.details.fields} confirm={p.details.confirm} />}
            {p.step === 2 && (
              <ProtectionStep
                products={p.protection.products}
                loading={p.protection.loading}
                notice={p.protection.notice}
                selectedValue={p.protection.selectedValue}
                onSelect={p.protection.onSelect}
                bgColor={p.companyDetails?.bg_color}
                proof={p.protection.proof}
              />
            )}
            {p.step === 3 && <ExtrasStep {...p.extras} bgColor={p.companyDetails?.bg_color} />}
            {p.step === REVIEW && (
              <ReviewStep
                car={{ name: p.car?.name, photo: p.photo }}
                pickup={p.trip.pickup}
                dropoff={p.trip.dropoff}
                protection={protectionLine}
                extras={extraLines}
                driver={{
                  firstName: p.details.fields.firstName.value,
                  lastName: p.details.fields.lastName.value,
                  email: p.details.fields.email.value,
                  phone: p.details.fields.phone.value,
                }}
                onEditTrip={p.onEditTrip}
                onEditDriver={() => p.onEditStep(1)}
                onEditProtection={() => p.onEditStep(2)}
                onEditExtras={hideExtras ? undefined : () => p.onEditStep(3)}
              />
            )}
            {p.step === PAY && (
              <>
                {p.codeGate ? (
                  <EmailCodeCard gate={p.codeGate} />
                ) : later ? (
                  <div className="rounded-2xl border border-secondary-100 bg-card-color p-4 text-sm">
                    <b className="line-clamp-2 break-words text-base" title={p.car?.name}>{p.car?.name}</b>
                    <span className="block opacity-70">{p.trip.range}</span>
                    <p className="mt-3">Reserve it now and pay later.</p>
                  </div>
                ) : (
                  <Suspense fallback={<p className="py-10 text-center text-sm opacity-70">Loading payment…</p>}>
                    <CheckOutPagePageMain
                      embedded
                      accepted={p.accepted}
                      payRef={payRef}
                      onPayState={setPay}
                      draft={p.payDraft}
                      onDraft={p.setPayDraft}
                    />
                  </Suspense>
                )}
                {!p.codeGate && (
                  <div className="mt-5">
                    <PolicyConsent
                      accepted={p.accepted}
                      onChange={p.setAccepted}
                      companyName={p.companyName}
                      active={p.open && p.step === PAY}
                      agreementUrl={agreementUrl}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <TotalBar
            total={total}
            onPriceDetails={() => setPriceOpen(true)}
            label={barLabel}
            labelTo={barTo}
            onClick={onBar}
            busy={busy}
            disabled={disabled}
            secondary={payLaterButton}
          />
        </div>
      )}

      <PriceDetailsSheet open={priceOpen} onClose={() => setPriceOpen(false)} invoice={bill} carName={p.car?.name} />
    </>,
    document.body,
  );
};

export default PhoneBooking;
